import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import { Box, chakra, usePrefersReducedMotion } from '@chakra-ui/react';

import { DeptGrid } from '@repo/ui-components/dept/design-system';
import ToggleVideoButton from '@repo/ui-components/dept/atoms/ToggleVideoButton';
import Image from '@repo/ui-components/tailwind/Image';
import { useUiComponentsInternationalContext } from '@repo/static-translations';

import SanityBackgroundImage from '../../atoms/SanityBackgroundImage';

/**
 * Generates the video URL with specific parameters based on the input URL.
 *
 * @param {string} url - The original video URL. e.g. https://www.youtube.com/watch?v=12345 or https://player.vimeo.com/video/12345?h=123
 * @returns {string} - The formatted video URL.
 */

const handleVideoUrl = (url, autoplay) => {
  if (!url) return null;

  const isVimeo = url.includes('vimeo');
  const paramSymbol = url.includes('&') ? '&' : '?';
  const backgroundParams = isVimeo
    ? '&background=1'
    : 'disablekb=1&enablejsapi=1&loop=1&modestbranding=1&color=white&controls=0&mute=1&rel=0';

  const autoplayParam = autoplay ? '&autoplay=1' : '&autoplay=0';

  if (isVimeo) {
    return `${url}${paramSymbol}${backgroundParams}${autoplayParam}`;
  }

  const videoId = url.includes('youtu.be')
    ? url.split('youtu.be/')[1]
    : url.split('v=')[1];

  return `https://www.youtube.com/embed/${videoId}${paramSymbol}${backgroundParams}${autoplayParam}&playlist=${videoId}`;
};

function MediaBackground({ background, children, inverted = false, ...props }) {
  const videoRef = useRef(null);
  const fileRef = useRef(null);
  const prefersReducedMotion = usePrefersReducedMotion();
  const { tt } = useUiComponentsInternationalContext();

  if (!background) return children;

  const isVideo = background?.mediaType === 'video';
  const isFile = background?.mediaType === 'videoFile';

  const handleVideoClick = status => {
    if (!videoRef.current) return;
    if (window === undefined) return;

    if (videoRef.current) {
      const iframe = videoRef.current;
      const url = iframe.src.split('?')[0];
      const isYoutube = url.includes('youtu');

      // Specify a target origin for this message
      if (isYoutube) {
        iframe.contentWindow.postMessage(
          `{"event":"command","func":"${
            status ? 'playVideo' : 'pauseVideo'
          }","args":""}`,
          url
        );
        return;
      }

      iframe.contentWindow.postMessage(
        `{"method":"${
          status ? 'play' : 'pause'
        }","value":"true","action":"play"}`,
        url
      );
    }
  };

  const toggleFilePlaying = () => {
    if (fileRef?.current) {
      if (fileRef.current.paused) {
        fileRef.current.play();
      } else {
        fileRef.current.pause();
      }
    }
  };

  const renderBackground = () => {
    if (background?.mediaType === 'videoFile') {
      return (
        <Box
          overflow="hidden"
          pos="relative"
          h="140%"
          w="auto"
          aspectRatio={{ base: '1.3', lg: 16 / 9 }}
          bottom="20%" // Center the video
          left={{ base: '50%', lg: 'auto' }}
          transform={{ base: 'translate(-50%)', lg: 'none' }}
        >
          <chakra.video
            data-content-type="fullscreenVideo"
            data-module-title={handleVideoUrl(
              background?.videoFile?.file?.asset?.url,
              !prefersReducedMotion
            )}
            h="full"
            w="auto"
            ref={fileRef}
            tabIndex={-1}
            autoPlay
            playsInline
            muted
            loop
          >
            <source src={background?.videoFile?.file?.asset?.url} />
            {background?.videoFile?.image && (
              <Image sanityImage={background.videoFile?.image} />
            )}
            {tt(s => s.mediaBackgroundComponent.downloadLabel)}
            <a href={background?.videoFile?.file?.asset?.url}>
              {tt(s => s.mediaBackgroundComponent.videoLink)}.
            </a>
            <track
              src="/noCaptions.vtt"
              kind="captions"
              srcLang="en"
              label="english captions"
            />
            {tt(s => s.mediaBackgroundComponent.unsupportedVideoLabel)}
          </chakra.video>
        </Box>
      );
    }

    if (background?.mediaType === 'video') {
      return (
        <Box
          ref={videoRef}
          pos="relative"
          as="iframe"
          src={handleVideoUrl(background?.video?.url, !prefersReducedMotion)}
          poster={background?.video?.thumbnail}
          h="140%"
          aspectRatio={{ base: '1.3', lg: 16 / 9 }}
          bottom="20%" // Center the video
          left={{ base: '50%', lg: 'auto' }}
          transform={{ base: 'translateX(-50%)', lg: 'none' }}
          tabIndex={-1}
        />
      );
    }

    return (
      <SanityBackgroundImage w="full" h="full" image={background?.image} />
    );
  };

  return (
    <DeptGrid
      pos="relative"
      w="full"
      minH={{ base: 'auto', lg: '38rem' }}
      borderRadius="lg"
      overflow="hidden"
      className="media-background"
      {...props}
    >
      {isVideo && (
        <ToggleVideoButton
          pos="absolute"
          top={4}
          right={inverted ? 'auto' : 4}
          left={inverted ? 4 : 'auto'}
          onClick={status => handleVideoClick(status)}
          zIndex={10}
        />
      )}
      {isFile && (
        <ToggleVideoButton
          pos="absolute"
          top={4}
          right={inverted ? 'auto' : 4}
          left={inverted ? 4 : 'auto'}
          onClick={toggleFilePlaying}
          zIndex={10}
        />
      )}
      <Box
        pos="absolute"
        top={0}
        left={0}
        h="full"
        w="full"
        zIndex={0}
        pointerEvents="none"
        tabIndex={-1}
        sx={{
          '&:before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            h: 'full',
            w: 'full',
            background:
              'linear-gradient(180deg, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 0.8) 100%), linear-gradient(0deg, rgba(0, 0, 0, 0.2), rgba(0, 0, 0, 0.2));',
            zIndex: 1,
          },
        }}
      >
        {renderBackground()}
      </Box>

      {children}
    </DeptGrid>
  );
}

MediaBackground.defaultProps = {
  inverted: false,
};

MediaBackground.propTypes = {
  background: PropTypes.shape({
    image: PropTypes.shape({
      alt: PropTypes.string,
      asset: PropTypes.shape({
        url: PropTypes.string,
      }),
    }),
    mediaType: PropTypes.string,
    video: PropTypes.shape({
      thumbnail: PropTypes.string,
      url: PropTypes.string,
    }),
    videoFile: PropTypes.shape({
      caption: PropTypes.string,
      file: PropTypes.shape({
        asset: PropTypes.shape({
          url: PropTypes.string,
        }),
      }),
      image: PropTypes.shape({
        asset: PropTypes.shape({
          _id: PropTypes.string,
          url: PropTypes.string,
        }),
        hotspot: PropTypes.shape({
          x: PropTypes.number,
          y: PropTypes.number,
        }),
        alt: PropTypes.string,
      }),
    }),
  }).isRequired,
  inverted: PropTypes.bool,
  children: PropTypes.node.isRequired,
};

export default MediaBackground;
